/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CourseHeader
 *
 * @file CourseHeader.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, Menu, MenuItem, Typography } from '@material-ui/core';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import { BUTTON_TYPES, POSITIONS } from '../../common/constants';
import Icon from '../icons/Icon';
import CustomCard from '../common/CustomCard';

const CourseCard = (props) => {
  const {
    course = {},
    classes = {},
    config = {},
    callback = {},
    children = null
  } = props;

  const {
    showClassSchedule = true,
    showCourseActions = true,
    showInstructor = true,
    showCourseTitle = true,
    showCourseMenu = true,
    showCourseHeaderActions = true,
    showInTwoColumns = false,
    showMetaData = true,
    showInviteStudents = true
  } = config;

  const { handleMenuOpenChange } = callback;

  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  /**
   * Handle menu open
   * @param {React.MouseEvent<HTMLButtonElement>} event
   * @returns {void}
   */
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
    handleMenuOpenChange?.();
  };

  /**
   * Handle menu close
   * @returns {void}
   */
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const renderTitle = () => {
    if (!showCourseTitle || !course.title) {
      return null;
    }

    return (
      <Typography className={classes.courseTitle}>
        {course.title}
      </Typography>
    );
  };

  const renderInstructor = () => {
    if (!showInstructor || !course.instructor) {
      return null;
    }

    return (
      <div className={classes.instructorContainer}>
        {(() => {
          if (course.instructorAvatarUrl) {
            return <img className={classes.avatar} src={course.instructorAvatarUrl} alt="Instructor avatar" />;
          }

          if (course.instructorAvatar) {
            return (
              <div className={classes.avatar}>
                <span>{course.instructorAvatar}</span>
              </div>
            );
          }

          return (
            <div className={classes.avatar}>
              <AccountCircleIcon />
            </div>
          );
        })()}

        <Typography className={classes.instructorName}>
          {course.instructor}
        </Typography>
      </div>
    );
  };

  const renderIconButton = (action) => {
    if (!action.icon && !action.iconName) {
      return null;
    }

    if (action.iconName) {
      return (
        <IconButton className={classes.iconButton} onClick={action.callback}>
          {action.showHotspot && <div className={classes.hotspot} />}
          <Icon name={action.iconName} />
        </IconButton>
      );
    }

    return (
      <IconButton className={classes.iconButton} onClick={action.callback}>
        {action.showHotspot && <div className={classes.hotspot} />}
        {action.icon}
      </IconButton>
    );
  };

  const renderTextButton = (action) => {
    if (!action.text) {
      return null;
    }

    return (
      <Button
        classes={{
          label: classes.secondaryButtonText,
          root: classes.secondaryButton,
          focusVisible: classes.focusVisible
        }}
        onClick={action.callback}
      >
        {action.text}
      </Button>
    );
  };


  const renderAction = (action) => {
    if (!action.callback) {
      return null;
    }

    if (action.type === BUTTON_TYPES.ICON) {
      return renderIconButton(action);
    }

    if (action.type === BUTTON_TYPES.BUTTON) {
      return renderTextButton(action);
    }

    return null;
  };

  const renderActions = (position = POSITIONS.BOTTOM) => {
    if (!showCourseActions || !course?.actions?.length) {
      return null;
    }

    const actions = course.actions.filter((action) => action.position === position);

    return actions.length ? (
      <div className={classes.actionsContainer}>
        {actions.map((action) => renderAction(action))}
      </div>
    ) : null;
  };

  const renderScheduleItem = (schedule) => {
    if (!schedule) {
      return null;
    }

    return (
      <div className={classes.scheduleItem}>
        {schedule.label && <Typography className={classes.scheduleItemTitle}>{schedule.label}</Typography>}
        {schedule.time && <Typography className={classes.scheduleItemTime}>{schedule.time}</Typography>}
      </div>
    );
  };

  const renderSchedule = () => {
    if (!showClassSchedule || !course.classSchedule) {
      return null;
    }

    return (
      <div className={classes.scheduleContainer}>
        {course?.classSchedule?.label
          && <Typography className={classes.scheduleTitle}>{course.classSchedule.label}</Typography>
        }
        <div className={classes.scheduleItemsContainer}>
          {course?.classSchedule?.schedule?.map((schedule) => renderScheduleItem(schedule))}
        </div>
      </div>
    );
  };

  const renderCourseMenu = () => {
    if (!course.courseMenu || !course.courseMenu.length) {
      return null;
    }

    return (
      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        getContentAnchorEl={null}
        classes={{
          paper: classes.leftAccordionListMenuPaper,
          list: classes.courseMenuRoot
        }}
      >
        {course.courseMenu.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              handleMenuClose();
              item.callback(item);
            }}
            classes={{
              root: `${classes.courseMenuItem} ${item.divider ? classes.courseMenuItemDivider : ''}`
            }}
          >
            <Typography>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    );
  };

  const renderCtaAction = (action) => {
    if (!action.callback) {
      return null;
    }

    if (action.iconName && action.text) {
      return (
        <Button
          className={classes.iconCtaButton}
          onClick={() => action.callback(action)}
          startIcon={<Icon name={action.iconName} className={classes.icon} />}
        >
          {action.text}
        </Button>
      );
    }

    return (
      <Button className={classes.button} onClick={() => action.callback(action)}>{action.text}</Button>
    );
  };

  const renderCourseMenuIcon = () => {
    if (!showCourseMenu) {
      return null;
    }

    return (
      <Fragment>
        <IconButton
          className={`${classes.iconButton} ${classes.courseMenuIcon} ${isMenuOpen ? classes.activeCourseMenuIcon : ''}`}
          onClick={handleMenuOpen}
        >
          <Icon name="ThreeDotMenuIcon" />
        </IconButton>

        {renderCourseMenu()}
      </Fragment>
    );
  };

  const renderCourseHeaderActions = (position = POSITIONS.TOP) => {
    if (!showCourseHeaderActions || !course?.actions?.length) {
      return null;
    }
    const actions = course.actions.filter((action) => action.position === position);

    return actions.length ? actions.map((action) => renderCtaAction(action)) : null;
  };

  const renderMetaDataItem = (meta) => {
    if (!meta) {
      return null;
    }

    return (
      <div className={classes.metaDataItem}>
        <span className={classes.metaDataLabel}>{meta.label}</span>
        <span className={classes.metaDataValue}>{meta.info}</span>
      </div>
    );
  };

  const renderMetaData = () => {
    if (!showMetaData || !course.metaData || !course.metaData.length) {
      return null;
    }

    return (
      <div className={classes.metaDataContainer}>
        {course.metaData.map((meta) => renderMetaDataItem(meta))}
      </div>
    );
  };

  const renderInviteStudents = () => {
    if (!showInviteStudents || !course.inviteStudents) {
      return null;
    }

    return (
      <Button className={classes.inviteStudentsButton} onClick={course.inviteStudents.callback}>
        {course.inviteStudents.text}
      </Button>
    );
  };

  if (showInTwoColumns) {
    return (
      <CustomCard
        className={`${classes.card} ${classes.courseCardTwoColumns}`}
      >
        <div className={classes.courseCardContent}>
          {renderTitle()}
          {renderMetaData()}
          <div className={classes.footerContainer}>
            {renderInviteStudents()}
            {renderCourseMenuIcon()}
          </div>
        </div>
      </CustomCard>
    );
  }

  return (
    <CustomCard
      className={`${classes.card} ${classes.courseCard}`}
    >
      <div className={classes.courseTitleContainer}>
        <div className={classes.actionContainer}>
          {renderTitle()}
          {renderCourseHeaderActions()}
        </div>
        {renderCourseMenuIcon()}
      </div>

      {renderSchedule()}
      <div className={classes.infoContainer}>
        {renderInstructor()}
        {renderActions()}
      </div>

      <div className={classes.courseChildrenContent}>
        {children}
      </div>
    </CustomCard>
  );
};

CourseCard.propTypes = {
  course: PropTypes.object,
  classes: PropTypes.object,
  config: PropTypes.object,
  callback: PropTypes.shape({
    handleMenuOpenChange: PropTypes.func
  }),
  children: PropTypes.node
};

CourseCard.defaultProps = {
  course: {},
  classes: {},
  config: {},
  callback: {},
  children: null
};

export default CourseCard;
